import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ScrollView } from 'react-native';
import { AnchoredSheet, AnchoredSheetTitleBar } from '@shared/components/AnchoredSheet';
import { View } from '@shared/components/View';
import { NativeDropdownButton } from '@shared/components/dropdown';
import { MBIconV2 } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1, MBTextBody1Bold } from '@shared/components/typography';
import { Batch } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext/BatchContext';
import { Colors, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { buildCustomRRule } from './utils';

export function YearOptionsMobile({
  rruleOptions,
  setRruleOptions,
}: Pick<Batch, 'rruleOptions' | 'setRruleOptions'>) {
  // Ensure we always have a valid reference date
  const dtstartDate = useMemo(() => {
    if (rruleOptions?.dtstart) {
      return new Date(rruleOptions.dtstart);
    }
    return new Date();
  }, [rruleOptions]);

  const defaultMonth = dtstartDate.getUTCMonth() + 1; // RRule months are 1-indexed
  const defaultDay = dtstartDate.getUTCDate();

  /*
   * Selected months derived from rruleOptions – fallback to start month if none.
   */
  const selectedMonths = useMemo<number[]>(() => {
    if (!rruleOptions?.bymonth) {
      return [defaultMonth];
    }
    if (Array.isArray(rruleOptions.bymonth)) {
      return rruleOptions.bymonth as number[];
    }
    return [rruleOptions.bymonth as number];
  }, [rruleOptions, defaultMonth]);

  useEffect(() => {
    if (isEmpty(rruleOptions?.bymonth)) {
      setRruleOptions(prev =>
        buildCustomRRule(
          prev,
          {
            bymonth: selectedMonths,
            bymonthday: !isNil(prev?.bymonthday) ? prev?.bymonthday : [defaultDay],
          },
          true,
        ),
      );
    }
  }, [rruleOptions?.bymonth, selectedMonths, defaultDay, setRruleOptions]);

  /*
   * Toggle a month in the selection.
   */
  const toggleMonth = useCallback(
    (monthNum: number) => {
      const isSelected = selectedMonths.includes(monthNum);
      let newSelection: number[];
      if (isSelected) {
        // Prevent clearing all months – must have at least one selected
        if (selectedMonths.length === 1) {
          return;
        }
        newSelection = selectedMonths.filter(m => m !== monthNum);
      } else {
        newSelection = [...selectedMonths, monthNum];
      }
      // Sort for deterministic ordering (Jan=1 … Dec=12)
      newSelection.sort((a, b) => a - b);
      setRruleOptions(prev =>
        buildCustomRRule(
          prev,
          {
            bymonth: newSelection,
            bymonthday: !isNil(prev?.bymonthday) ? prev?.bymonthday : [defaultDay],
          },
          true,
        ),
      );
    },
    [selectedMonths, defaultDay, setRruleOptions],
  );

  const monthsDisplay = useMemo(() => {
    const names = selectedMonths.map(m =>
      moment()
        .month(m - 1)
        .format('MMMM'),
    );
    return names.join(', ');
  }, [selectedMonths]);

  // Dropdown / Action-sheet state
  const anchorName = '--year-options-month-select';
  const [showMonthSheet, setShowMonthSheet] = useState(false);

  const handleMonthMenuOpen = () => {
    setShowMonthSheet(true);
  };

  const handleCloseSheet = () => {
    setShowMonthSheet(false);
  };

  return (
    <View>
      <MBTextBody1Bold style={{ marginBottom: grid(1) }}>
        {i18n('workflowTemplate.component.dateAndRepeat.onDotDotDot')}
      </MBTextBody1Bold>
      <View row centerVertically paddingTop={1} paddingBottom={1} style={{ width: '100%' }}>
        <View
          style={{ anchorName, flexGrow: 1, marginRight: grid(1), marginLeft: 0, maxWidth: '85%' }}
        >
          <NativeDropdownButton onPress={handleMonthMenuOpen} disabled={!showMonthSheet}>
            {monthsDisplay}
          </NativeDropdownButton>
        </View>
        <MBTextBody1 style={{ flexShrink: 0 }}>
          {moment.localeData().ordinal(defaultDay)}
        </MBTextBody1>
      </View>
      {showMonthSheet && (
        <AnchoredSheet
          initialSize="full"
          onClose={handleCloseSheet}
          anchorName={anchorName}
          renderTitleBar={() => (
            <AnchoredSheetTitleBar
              title={i18n('workflowTemplate.component.dateAndRepeat.selectMonths')}
              rightButtonText={i18n('common.done')}
              onRightIconPress={handleCloseSheet}
            />
          )}
        >
          <ScrollView>
            {moment.months().map((monthName, idx) => {
              const monthNum = idx + 1;
              const isSelected = selectedMonths.includes(monthNum);
              return (
                <MBTouchableOpacity
                  key={monthName}
                  onPress={() => toggleMonth(monthNum)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingHorizontal: grid(3),
                    paddingVertical: grid(1.5),
                  }}
                >
                  <MBTextBody1>{monthName}</MBTextBody1>
                  <MBIconV2
                    name={isSelected ? 'check-box' : 'check-box-empty'}
                    color={Colors.v2.green}
                    size={grid(4)}
                  />
                </MBTouchableOpacity>
              );
            })}
          </ScrollView>
        </AnchoredSheet>
      )}
    </View>
  );
}
